// The e-sign flow's DERIVED and SHARED copy.
//
// `EsignApp` is a long single-file instrument and most of its prose is written
// where it is rendered, once — a string used in exactly one place cannot drift
// out of step with a copy of itself. What is here is the rest: a line derived
// from live state, one set from four call sites, and the failure/recovery pair
// whose whole point is that they are DIFFERENT lines.

/**
 * Set on an unresolvable document identity, a failed fetch, and an opening
 * response missing its Section 8/9 content — every path where signing must fail
 * closed. NOT set on an expired signer session, which is recoverable.
 */
export const DOCUMENT_DISPLAY_ERROR =
  "The consent document can’t be displayed correctly. Contact your practice.";

/**
 * The progress line beside the Sign action. The button stays ENABLED while items
 * are outstanding, so this count is what carries the incomplete state — and the
 * button points at it with `aria-describedby`.
 */
export function completionStatusLabel(remainingCount: number): string {
  return remainingCount === 0
    ? "All required items complete"
    : `${remainingCount} required ${remainingCount === 1 ? "item" : "items"} remaining`;
}

/** Shown when the typed signature does not match the verified legal name. */
export function signatureMismatchMessage(signerName: string): string {
  return `Your signature must match your full legal name (${signerName}).`;
}

/**
 * A WARNING, not an error: nothing is broken and no signature was lost. It must
 * never send the signer to their practice — that is `DOCUMENT_DISPLAY_ERROR`.
 */
export const SESSION_EXPIRED_MESSAGE =
  "Your secure session timed out while you finished the other questions. Send yourself a fresh code and you can pick up right where you left off — nothing has been lost.";

/** The recovery affordance that pairs with it — a code request, not a dead end. */
export const SEND_NEW_CODE_LABEL = "Send a new verification code";

/**
 * The hourly send cap, refused as 429 (`sendCodeStatus.ts`). Here rather than at its
 * call site because it is now written to TWO message channels from one place: the send
 * screen shows `requestError`, the code screen shows `codeError`, and a resend can be
 * refused while the client is standing on either. One string, so the two cannot drift.
 *
 * RECOVERABLE, and phrased as such. It must never read like `DOCUMENT_DISPLAY_ERROR`:
 * nothing is broken, nobody needs to phone their practice, and the wait clears itself.
 */
export const THROTTLED_SEND_MESSAGE =
  "Too many codes have been requested for this document in the last hour. Try again shortly.";
